import { Disposable, toDisposable } from '../../../base/common/lifecycle.js';
import { BUILD_ENVIRONMENT, BUILD_HASH, BUILD_PLATFORM, BUILD_SEMVER } from '../../../base/common/$__metadata.js';
import type { LongFrameLike } from '../../../base/browser/longAnimationFrame.js';
import { IAnalyticsService } from '../../analytics/common/analytics.js';
import { ITracingService, type ITraceSpan } from './tracing.js';
import { IUserConfiguration } from '../../../editor/services/user-configuration/common/userConfiguration.js';

interface OperationSummary {
    name: string;
    count: number;
    failed: number;
    slow: number;
    totalMs: number;
    maxMs: number;
}
interface FrameSummary {
    scope: 'page';
    durationMs: number;
    blockingMs: number;
    source?: string;
    charPosition?: number;
}

const DIAGNOSTIC_FLUSH_MS = 60_000;
const HEALTHY_FLUSH_MS = 300_000;

export class TracingService extends Disposable implements ITracingService {
    private operations = new Map<string, OperationSummary>();
    private frames = { count: 0, totalMs: 0, maxMs: 0, worst: [] as FrameSummary[] };
    private urgent = false;
    private lastFlushAt = performance.now();
    private visibilityEpoch = 0;
    private visibleSince = 0;

    private get inactive(): boolean {
        // Frames belong to the measured page, even when its editor is popped out.
        return this.config?.uiLayout === 'minimal'
            || (typeof document !== 'undefined' && document.visibilityState === 'hidden');
    }

    constructor(
        @IAnalyticsService private readonly analytics: IAnalyticsService,
        @IUserConfiguration private readonly config?: IUserConfiguration,
    ) {
        super();
        if (typeof document !== 'undefined') {
            const measuredDocument = document;
            const visibilityChanged = () => {
                this.resetActivity();
                if (this.config?.uiLayout !== 'minimal') {
                    this.analytics.logEvent('editor.visibility', this.activityContext());
                }
            };
            measuredDocument.addEventListener('visibilitychange', visibilityChanged);
            this._register(toDisposable(() => measuredDocument.removeEventListener('visibilitychange', visibilityChanged)));
        }
        if (config) {
            let layout = config.uiLayout;
            let surface = config.workspaceSurface;
            this._register(config.onDidChange(() => {
                const layoutChanged = layout !== config.uiLayout;
                if (!layoutChanged && surface === config.workspaceSurface) return;
                layout = config.uiLayout;
                surface = config.workspaceSurface;
                this.resetActivity();
                if (layoutChanged) this.analytics.logEvent('editor.layout', this.activityContext());
            }));
        }
        const timer = setInterval(() => {
            if (this.urgent || performance.now() - this.lastFlushAt >= HEALTHY_FLUSH_MS) this.flush();
        }, DIAGNOSTIC_FLUSH_MS);
        this._register(toDisposable(() => { clearInterval(timer); this.flush(); }));
    }

    startSpan(name: string): ITraceSpan {
        const start = performance.now();
        const epoch = this.visibilityEpoch;
        const startedInactive = this.inactive;
        let ended = false;
        return { end: outcome => {
            if (ended || this.isDisposed) return;
            ended = true;
            if (startedInactive || this.inactive || epoch !== this.visibilityEpoch) return;
            const duration = Math.max(0, performance.now() - start);
            const key = this.operations.has(name) || this.operations.size < 50 ? name : 'other';
            let summary = this.operations.get(key);
            if (!summary) {
                summary = { name: key, count: 0, failed: 0, slow: 0, totalMs: 0, maxMs: 0 };
                this.operations.set(key, summary);
            }
            summary.count++;
            summary.failed += outcome === 'failed' ? 1 : 0;
            summary.slow += duration >= 100 ? 1 : 0;
            summary.totalMs += duration;
            summary.maxMs = Math.max(summary.maxMs, duration);
            if (outcome === 'failed' || duration >= 100) this.urgent = true;
        } };
    }

    recordLongFrame(frame: LongFrameLike): void {
        const duration = frame.duration ?? 0;
        if (this.isDisposed || this.inactive || (frame.startTime !== undefined && frame.startTime < this.visibleSince)
            || !Number.isFinite(duration) || duration < 50) return;
        this.urgent = true;
        this.frames.count++;
        this.frames.totalMs += duration;
        this.frames.maxMs = Math.max(this.frames.maxMs, duration);
        const top = frame.scripts?.reduce<typeof frame.scripts[number] | undefined>(
            (a, b) => !a || (b.duration ?? 0) > (a.duration ?? 0) ? b : a,
            undefined,
        );
        const source = safeSource(top?.sourceURL);
        this.frames.worst.push({
            scope: 'page', durationMs: Math.round(duration), blockingMs: Math.round(frame.blockingDuration ?? 0),
            ...(source ? { source } : {}),
            ...(typeof top?.sourceCharPosition === 'number' ? { charPosition: top.sourceCharPosition } : {}),
        });
        this.frames.worst.sort((a, b) => b.durationMs - a.durationMs);
        this.frames.worst.length = Math.min(3, this.frames.worst.length);
    }

    private flush(): void {
        if (this.inactive) { this.reset(); return; }
        if (!this.operations.size && !this.frames.count) return;
        const intervalMs = Math.max(0, Math.round(performance.now() - this.lastFlushAt));
        const operations = [...this.operations.values()].map(s => ({ ...s, totalMs: Math.round(s.totalMs), maxMs: Math.round(s.maxMs) }));
        const frames = this.frames;
        this.reset();
        this.analytics.logEvent('performance.summary', {
            version: BUILD_SEMVER, hash: BUILD_HASH, platform: BUILD_PLATFORM, build: BUILD_ENVIRONMENT,
            ...this.activityContext(),
            intervalMs, timing: 'elapsed', operations, frames,
        });
    }

    private activityContext() {
        return {
            editorLayout: this.config?.uiLayout ?? 'unknown',
            editorSurface: this.config?.workspaceSurface ?? 'unknown',
            pageVisibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
            pageFocused: typeof document !== 'undefined' ? document.hasFocus() : false,
        };
    }

    private resetActivity(): void {
        this.visibilityEpoch++;
        this.visibleSince = performance.now();
        this.reset();
    }

    private reset(): void {
        this.operations.clear();
        this.urgent = false;
        this.lastFlushAt = performance.now();
        this.frames = { count: 0, totalMs: 0, maxMs: 0, worst: [] };
    }
}

function safeSource(source: string | undefined): string | undefined {
    if (!source) return undefined;
    try {
        const url = new URL(source);
        if (!['http:', 'https:', 'chrome-extension:'].includes(url.protocol)) return undefined;
        return `${url.protocol}//${url.host}${url.pathname}`;
    } catch { return undefined; /* Inline scripts and non-URL attribution have no safe source URL. */ }
}
